/**
 * SyncStatusPanel — statut en direct d'une synchronisation ETL.
 *
 * PERSISTANCE : Ce composant est autonome. Il s'affiche automatiquement
 * quand un log existe (running ou terminé il y a < 10 min).
 * Ne dépend pas d'un activeLogId local — survit à la navigation.
 *
 * DURÉE : temps écoulé (mesuré) + durée attendue (médiane des 5 derniers runs
 * racines comparables, calculée serveur). Quand le serveur ne sait pas — moins de
 * 3 runs comparables, ou dispersion supérieure à un facteur 3 — il renvoie
 * `estimate: null` et l'écran n'affiche QUE le temps écoulé, avec la raison.
 *
 * AUCUNE BARRE DE PROGRESSION, AUCUN INDICATEUR DE PHASE — délibéré.
 * On connaît le temps écoulé et un historique, JAMAIS l'avancement réel : le
 * pipeline ne rapporte pas où il en est. L'ancienne barre affichait
 * `elapsed / estimé`, et le stepper de phases `floor(elapsed / estimé × 4)` — deux
 * façons de présenter la même extrapolation comme une mesure. Une barre qui ment
 * est pire que pas de barre. Ne pas les réintroduire sans un vrai signal
 * d'avancement émis par le runner.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type EstimateReason,
  SYNC_RUNNING_QUERY_KEY,
  useSyncRunningStatus,
} from '../hooks/useSyncRunningStatus';
import { api } from '../services/api';
import { parseMessage, type ParsedStep } from './syncStatusMessage';

/** Un log terminé reste affiché ce nombre de minutes, puis le panneau disparaît. */
const STALE_MIN = 10;

const ESTIMATE_REASON_LABEL: Record<EstimateReason, string> = {
  insufficient_data: 'pas assez d’historique comparable',
  too_dispersed: 'durées trop dispersées pour estimer',
};

interface Props {
  onDone?: () => void;
}

// NOTE — la liste EXPECTED_STEPS (36 étapes analytics + 3 admin) et PHASE_ORDER
// ont été retirées avec le stepper de phases : leur seul usage était de découper
// `elapsed / estimé` en 4 segments pour simuler un avancement. Elles dupliquaient
// en dur le pipeline de packages/etl et dérivaient à chaque changement de
// transform. Les étapes réellement exécutées viennent du run lui-même.

// ─── Utilitaires ──────────────────────────────────────────────────

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

// ─── Composant principal ──────────────────────────────────────────

export function SyncStatusPanel({ onDone }: Props) {
  // Horloge — seul « systeme externe » de ce panneau. `nowMs` est la SEULE
  // lecture de l'heure : le rendu n'appelle plus `Date.now()` (impur : deux
  // rendus du meme etat rendaient deux resultats differents).
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [expanded, setExpanded] = useState(false);
  const onDoneRef = useRef(onDone);
  // Ecrire une ref PENDANT le rendu casse le rendu concurrent (un rendu
  // abandonne laisse la ref ecrite) : on la rafraichit apres commit.
  useEffect(() => {
    onDoneRef.current = onDone;
  });

  const { data } = useSyncRunningStatus();

  const queryClient = useQueryClient();

  const killMutation = useMutation({
    mutationFn: () => api.post<{ ok: boolean; killed: number }>('/api/syncs/kill', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SYNC_RUNNING_QUERY_KEY });
    },
  });

  const log = data?.log ?? null;
  const estimate = data?.estimate ?? null;
  const estimateReason = data?.estimateReason ?? null;
  const isRunning = log?.status === 'running';

  const startMs = log?.started_at ? new Date(log.started_at).getTime() : null;
  const endedMs = !isRunning && log?.ended_at ? new Date(log.ended_at).getTime() : null;

  // ── Chronomètre ──────────────────────────────────────────────────
  // L'intervalle ne fait qu'avancer l'horloge ; le temps écoulé et l'âge du log
  // en sont DÉRIVÉS (aucun état à écrire depuis l'effet).
  useEffect(() => {
    const needsClock = isRunning || endedMs !== null;
    if (!needsClock) return;
    const id = setInterval(() => {
      const t = Date.now();
      setNowMs(t);
      // Un log terminé disparaît à STALE_MIN : passé ce point, plus rien à compter.
      if (!isRunning && endedMs !== null && t - endedMs > STALE_MIN * 60_000) {
        clearInterval(id);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [isRunning, endedMs]);

  // Durée finale = valeur du log ; sinon décompte depuis le départ.
  const elapsedSec =
    !isRunning && log?.duration_ms != null
      ? Math.round(log.duration_ms / 1000)
      : startMs !== null
        ? Math.max(0, Math.round((nowMs - startMs) / 1000))
        : 0;

  // ── Callback quand terminé ────────────────────────────────────────
  useEffect(() => {
    if (log && log.status !== 'running') onDoneRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log?.status]);

  // ── Masquer si rien ou log trop vieux (> STALE_MIN après fin) ─────
  if (!log) return null;
  if (endedMs !== null && (nowMs - endedMs) / 60_000 > STALE_MIN) return null;

  // ── Durée attendue (jamais un avancement) ─────────────────────────
  // `estimatedSec` sert UNIQUEMENT à afficher un ordre de grandeur à côté du
  // temps écoulé. Il n'est jamais converti en pourcentage ni en position.
  const estimatedSec = estimate ? Math.round(estimate.durationMs / 1000) : null;

  const isOvertime = isRunning && estimatedSec !== null && elapsedSec > estimatedSec;

  // ── Étapes depuis le message (disponible après fin du run) ────────
  const completedSteps: ParsedStep[] = log.message ? parseMessage(log.message) : [];
  const doneCount = completedSteps.length;

  // ── Compteurs par statut ──────────────────────────────────────────
  const errCount = completedSteps.filter((s) => s.status === 'error').length;
  const okCount = completedSteps.filter((s) => s.status === 'done').length;
  // Les étapes sautées sont comptées SÉPARÉMENT : les noyer dans « OK » redonnerait
  // au run l'apparence complète qu'il n'a pas (ADR-075).
  const skipCount = completedSteps.filter((s) => s.status === 'skipped').length;

  // ── Couleurs selon statut ─────────────────────────────────────────
  const isError = log.status === 'error';
  const isOk = log.status === 'ok';
  const isPartial = log.status === 'partial';

  const borderColor = isRunning
    ? 'border-blue-200 dark:border-blue-800'
    : isOk
      ? 'border-green-200 dark:border-green-800'
      : isPartial
        ? 'border-amber-200 dark:border-amber-800'
        : isError
          ? 'border-red-200 dark:border-red-800'
          : 'border-slate-200 dark:border-slate-700';

  const timeColor = isRunning
    ? 'text-blue-600 dark:text-blue-400'
    : isOk
      ? 'text-green-600 dark:text-green-400'
      : isPartial
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-red-600 dark:text-red-400';

  return (
    <div
      className={`rounded-xl border bg-white dark:bg-slate-900 shadow-xs overflow-hidden ${borderColor}`}
    >
      {/* ── En-tête ────────────────────────────────────────────────── */}
      <div className="px-4 py-3 flex items-center gap-3">
        {/* Icône statut */}
        {isRunning ? (
          <div className="h-4 w-4 shrink-0 rounded-full border-[2.5px] border-blue-500 border-t-transparent animate-spin" />
        ) : isOk ? (
          <div className="h-4 w-4 shrink-0 rounded-full bg-green-50 dark:bg-green-950/40 ring-1 ring-green-200/80 dark:ring-green-800 flex items-center justify-center">
            <svg
              viewBox="0 0 10 10"
              className="w-2.5 h-2.5 text-green-600 dark:text-green-400 fill-none stroke-current stroke-[1.5]"
            >
              <polyline points="2,5 4,7.5 8,3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        ) : isPartial ? (
          <div
            className="h-4 w-4 shrink-0 rounded-full bg-brand-50 dark:bg-brand-900/40 ring-1 ring-brand-200/80 dark:ring-brand-800 flex items-center justify-center"
            title={`${okCount} OK · ${errCount} erreur${errCount > 1 ? 's' : ''}`}
          >
            <svg viewBox="0 0 10 10" className="w-2 h-2 text-brand-600 dark:text-brand-400 fill-current">
              <path d="M5 1 L9 9 L1 9 Z" />
            </svg>
          </div>
        ) : (
          <div className="h-4 w-4 shrink-0 rounded-full bg-red-50 dark:bg-red-950/40 ring-1 ring-red-200/80 dark:ring-red-800 flex items-center justify-center">
            <svg viewBox="0 0 10 10" className="w-2 h-2 text-red-600 dark:text-red-400 fill-none stroke-current stroke-[2]">
              <line x1="2" y1="2" x2="8" y2="8" strokeLinecap="round" />
              <line x1="8" y1="2" x2="2" y2="8" strokeLinecap="round" />
            </svg>
          </div>
        )}

        {/* Texte principal */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{log.app_name}</span>
            <span className="text-xs px-1.5 py-0.5 rounded-sm bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 font-mono">
              {log.direction}
            </span>
            {isRunning && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-medium">
                En cours
              </span>
            )}
            {isOk && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-300 font-medium">
                Terminé
              </span>
            )}
            {isPartial && (
              <span
                className="text-xs px-1.5 py-0.5 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 font-medium"
                title={`${okCount}/${doneCount} steps OK · ${errCount} échec${errCount > 1 ? 's' : ''} — voir détail ci-dessous`}
              >
                Partiel
              </span>
            )}
            {isError && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300 font-medium">
                Erreurs
              </span>
            )}
          </div>

          <div className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
            {isRunning ? (
              <>
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  {estimatedSec !== null
                    ? isOvertime
                      ? `${formatTime(elapsedSec)} écoulées — dépasse les ~${formatTime(estimatedSec)} estimées`
                      : `${formatTime(elapsedSec)} écoulées sur ~${formatTime(estimatedSec)} estimées`
                    : `${formatTime(elapsedSec)} écoulées`}
                </span>
                {estimatedSec === null && estimateReason && (
                  <span className="ml-2 text-slate-500 dark:text-slate-500">
                    ({ESTIMATE_REASON_LABEL[estimateReason]})
                  </span>
                )}
              </>
            ) : (
              <>
                {okCount > 0 && (
                  <span className="text-green-600 dark:text-green-400">{okCount} OK</span>
                )}
                {errCount > 0 && (
                  <span className="ml-2 text-red-600 dark:text-red-400">
                    {errCount} erreur{errCount > 1 ? 's' : ''}
                  </span>
                )}
                {log.records != null && (
                  <span className="ml-2">{log.records.toLocaleString('fr-CA')} records</span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Chronomètre */}
        <div className={`font-mono text-xl font-bold tabular-nums shrink-0 ${timeColor}`}>
          {formatTime(elapsedSec)}
        </div>

        {/* Bouton Kill — visible seulement pendant un run actif */}
        {isRunning && (
          <button
            type="button"
            onClick={() => {
              if (confirm('Annuler le sync en cours ?')) killMutation.mutate();
            }}
            disabled={killMutation.isPending}
            className="shrink-0 text-xs px-2 py-1 rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40 touch-manipulation transition-colors"
          >
            {killMutation.isPending ? '...' : 'Annuler'}
          </button>
        )}
      </div>

      {/* ── Repères temporels ──────────────────────────────────────────
          Volontairement PAS de barre de progression : rien dans le pipeline ne
          rapporte son avancement réel (voir en-tête de fichier). */}
      <div className="px-4 pb-2">
        <div className="flex justify-between text-meta text-slate-500 dark:text-slate-500 tabular-nums">
          {isRunning ? (
            <>
              <span>
                Démarré à{' '}
                {new Date(log.started_at).toLocaleTimeString('fr-CA', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              {estimatedSec !== null ? (
                <span>
                  Attendu ~{formatTime(estimatedSec)} (médiane de {estimate?.basedOnCount} runs)
                </span>
              ) : (
                <span className="italic">Aucune estimation fiable</span>
              )}
            </>
          ) : (
            <>
              <span>
                {new Date(log.started_at).toLocaleString('fr-CA', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </span>
              {log.duration_ms != null && (
                <span>Durée : {formatTime(Math.round(log.duration_ms / 1000))}</span>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Détail des étapes (après fin, expandable) ─────────────── */}
      {completedSteps.length > 0 && (
        <div className="border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-full px-4 py-2 flex items-center justify-between text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-colors touch-manipulation"
          >
            <span className="font-medium">
              {doneCount} étapes · {okCount} OK
              {errCount > 0 && ` · ${errCount} erreur${errCount > 1 ? 's' : ''}`}
              {skipCount > 0 && ` · ${skipCount} sautée${skipCount > 1 ? 's' : ''}`}
            </span>
            <svg
              viewBox="0 0 10 10"
              className={`w-3 h-3 fill-none stroke-current stroke-[1.5] transition-transform ${expanded ? 'rotate-180' : ''}`}
            >
              <polyline points="2,3.5 5,6.5 8,3.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {expanded && (
            <div className="max-h-60 overflow-y-auto px-4 pb-3 space-y-px">
              {completedSteps.map((step, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-2 py-1 text-xs rounded-sm px-1 ${
                    step.status === 'error'
                      ? 'text-red-700 dark:text-red-400'
                      : step.status === 'skipped'
                        ? 'text-amber-700 dark:text-amber-500'
                        : 'text-slate-600 dark:text-slate-400'
                  }`}
                >
                  {step.status === 'error' ? (
                    <span className="shrink-0 mt-px text-red-500">✗</span>
                  ) : step.status === 'skipped' ? (
                    // Ni coche ni croix : l'étape n'a pas échoué, elle n'a pas calculé.
                    <span className="shrink-0 mt-px text-amber-500">⊘</span>
                  ) : (
                    <span className="shrink-0 mt-px text-green-500">✓</span>
                  )}

                  <span className="font-mono shrink-0">{step.name}</span>

                  <span className="ml-auto shrink-0 tabular-nums text-slate-500 dark:text-slate-500">
                    {step.status === 'error' ? (
                      <span
                        className="text-red-500 text-chip max-w-52 truncate block text-right"
                        title={step.errorMsg}
                      >
                        {step.errorMsg}
                      </span>
                    ) : step.status === 'skipped' ? (
                      <span
                        className="text-amber-600 dark:text-amber-500 text-chip max-w-52 truncate block text-right"
                        title={step.errorMsg}
                      >
                        {step.errorMsg}
                      </span>
                    ) : step.rows !== null ? (
                      `${step.rows.toLocaleString('fr-CA')} rows`
                    ) : (
                      ''
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
